/**
 * Which /admin paths the `designer` role may open.
 *
 * ## This is UX, not security
 *
 * Everything here is enforced again on the server by `adminMiddleware` and the
 * `requireCapability()` gates behind it (see functions/src/lib/admin-surface.ts).
 * A designer who edits this list in devtools gets an empty page and a wall of
 * 403s, not data. The reason to have it at all is that landing on a permanently
 * empty Orders screen is a bad experience and reads as a bug.
 *
 * Do not let anyone "simplify" by removing the server-side check because this
 * one exists.
 *
 * ## Fail closed
 *
 * `isDesignerPath` allows only what is listed. AdminLayout has one guard in
 * front of ~200 routes, so a route added tomorrow is denied until someone adds
 * it here — the same property the backend surface table has, for the same
 * reason.
 */

/**
 * Path prefixes a designer may open. Matched on segment boundaries, so
 * '/admin/pages' covers '/admin/pages/:id' but never '/admin/pages-secret'.
 *
 * Note '/admin' itself is deliberately absent: the dashboard is revenue and
 * order metrics, which is not a designer's business.
 */
export const DESIGNER_PATH_PREFIXES: readonly string[] = [
  '/admin/design-start',
  '/admin/pages',
  '/admin/homepage',
  '/admin/media',
  '/admin/settings/theme',
  '/admin/settings/sandbox',
  '/admin/settings/branding',
  '/admin/settings/design-history',
  '/admin/settings/design-checklist',
];

/**
 * Where a designer lands after login, and where a denied deep link redirects.
 *
 * The start page rather than the theme editor: an outside contractor opening an
 * admin panel with two visible sections and eighteen missing ones assumes their
 * permissions are broken. This page says what the account can do, what it
 * deliberately cannot, and where the four tools are.
 */
export const DESIGNER_HOME = '/admin/design-start';

export function isDesignerPath(pathname: string): boolean {
  return DESIGNER_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
  );
}
